import { memo, useEffect } from "react";
import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import { translateTopMenuItems } from "byzantine/src/l10n/SettingLocalizers";

import NavBar from "../NavBar";
import { TopMenuProps } from "./types";
import {
  isAccountItem,
  isAppItem,
  isLinkItem,
  isNafItem,
  isNestedMenuItem,
} from "./utils";
import { useTopMenuContainer } from "./topMenu.container";

import NAFItem from "./components/nafItem.component";
import LinkItem from "./components/linkItem.component";
import AccountItem from "./components/account.component";
import AppItem from "./components/appItem.component";
import NestedItem from "./components/nestedItem.component";
import { Bootstrap } from "./topMenu.bootstrap";

function TopMenuComponent(props: TopMenuProps) {
  const { l10n } = useLocalization();
  const {
    menuItems,
    institution,
    features,
    orderedAccounts,
    legacyNafUrls,
    isLoggedIn,
  } = useTopMenuContainer(props);

  const { load } = Bootstrap.useBootstrap();
  const { send: loadAccounts } = entities.accounts.useFetchAccounts();

  useEffect(() => {
    load();
    if (isLoggedIn) {
      loadAccounts();
    }
  }, [isLoggedIn]);

  const translatedMenuItems = translateTopMenuItems<API.AnyMenuItem>(
    menuItems,
    l10n
  );

  // NavBar takes children to represent top-level navigation items.
  return (
    <NavBar
      features={features}
      deviceIsRemembered={props.deviceIsRemembered}
      institution={institution}
    >
      {translatedMenuItems
        .map((item) => {
          if (isAccountItem(item.itemProps)) {
            return (
              <AccountItem
                item={{ name: item.name, itemProps: item.itemProps }}
                orderedAccounts={orderedAccounts}
                key={item.name}
              />
            );
          }

          if (isLinkItem(item.itemProps)) {
            return (
              <LinkItem
                item={{ name: item.name, itemProps: item.itemProps }}
                key={item.name}
              />
            );
          }

          if (isAppItem(item.itemProps)) {
            return (
              <AppItem
                item={{ name: item.name, itemProps: item.itemProps }}
                key={item.name}
              />
            );
          }

          if (isNafItem(item.itemProps)) {
            return <NAFItem legacyNafUrls={legacyNafUrls} key={item.name} />;
          }

          if (isNestedMenuItem(item.itemProps)) {
            return (
              <NestedItem
                item={{ name: item.name, itemProps: item.itemProps }}
                legacyNafUrls={legacyNafUrls}
                key={item.name}
              />
            );
          }

          return null;
        })
        .filter(Boolean)}
    </NavBar>
  );
}

export default Bootstrap.WrapComponent(memo(TopMenuComponent));
